import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Typography, IconButton, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { ImageAsset as TImageAsset, Maybe, BffBrandType, AvailableSlot } from '@/types/generated';
import { fetchTimes, formatDisplayTime, getWeekOut } from './SchedulingTimes.helpers';
import { schedulingTimesContentWrapperStyles, schedulingTimesImageStyles, schedulingTimesListStyles, schedulingTimesWrapperStyles, timeMessageStyles } from './SchedulingTimes.styles';
import ImageAsset from '@shared-components/components/ImageAsset';
import { IImage } from '@shared-components/components/ImageAsset/ImageAsset';
import { getMediaContainerStyles } from '@shared-components/components/ServiceLineHero/ServiceLineHero.styles';
import TAGButton from '@shared-components/components/TAGButton';
import TAGLink from '@shared-components/components/TAGLink';
import { useAppContext } from '@shared-components/context';
import { TRoundedCornersSize } from '@shared-components/types';
interface SchedulingTimesProps {
  roundedCornerSize?: Maybe<TRoundedCornersSize>;
  componentTheme: Theme;
  schedulingImageAsset?: Maybe<TImageAsset>;
  brand: BffBrandType;
  facilityId: string;
  scheduleAppointmentURL?: Maybe<string>;
}
export function SchedulingTimesList({
  slotsList = [],
  scheduleAppointmentURL = '',
  loading = false
}: Readonly<{
  slotsList: AvailableSlot[];
  scheduleAppointmentURL?: Maybe<string>;
  loading?: boolean;
  facilityId?: string;
}>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const itemsPerPage = isMobile ? 9 : 4;
  const totalPages = Math.ceil(slotsList.length / itemsPerPage);
  const [mobileDisplayedItems, setMobileDisplayedItems] = useState<number>(itemsPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  useEffect(() => {
    setCurrentPage(0);
    setMobileDisplayedItems(itemsPerPage);
  }, [slotsList, itemsPerPage]);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = isMobile ? mobileDisplayedItems : startIndex + itemsPerPage;
  const currentSlots = slotsList.slice(startIndex, endIndex);
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };
  const handleLoadMore = () => {
    setMobileDisplayedItems(prev => prev + itemsPerPage);
  };
  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;
  return <Box data-testid="scheduling-times-list-container" data-sentry-element="Box" data-sentry-component="SchedulingTimesList" data-sentry-source-file="SchedulingTimes.tsx">
			<Box data-testid="scheduling-times-list" sx={schedulingTimesListStyles} data-sentry-element="Box" data-sentry-source-file="SchedulingTimes.tsx">
				{!isMobile && currentSlots.length > 0 && <IconButton onClick={handlePreviousPage} disabled={!canGoPrevious} aria-label="Previous time slots" data-testid="previous-slots-button" size="small" sx={{
        color: 'text.primary'
      }}>
						<ChevronLeft />
					</IconButton>}
				<Box data-testid="scheduling-times-slots" sx={{
        display: 'flex',
        flexWrap: {
          xs: 'wrap',
          md: 'nowrap'
        },
        justifyContent: {
          xs: 'center',
          md: 'flex-start'
        },
        gap: '0.75rem',
        width: {
          xs: '100%',
          md: 'auto'
        }
      }} data-sentry-element="Box" data-sentry-source-file="SchedulingTimes.tsx">
					{loading && Array.from({
          length: itemsPerPage
        }).map((_, index) => <Skeleton variant="rounded" width={96} height={52} key={`loading-skeleton-${index}`} />)}
					{!loading && currentSlots.map(slot => <TAGButton href={`${scheduleAppointmentURL ?? ''}&timeSlot=${slot.date}T${slot.time}`} key={`scheduling-link-${slot.date}-${slot.time}`} variant="secondaryDefault" sx={{
          width: '96px',
          height: '52px',
          padding: 0
        }}>
								{formatDisplayTime(slot.time)}
							</TAGButton>)}
				</Box>
				{!isMobile && currentSlots.length > 0 && <IconButton onClick={handleNextPage} disabled={!canGoNext} aria-label="Next time slots" data-testid="next-slots-button" size="small" sx={{
        color: 'text.primary'
      }}>
						<ChevronRight />
					</IconButton>}
			</Box>
			{isMobile && mobileDisplayedItems < slotsList.length && <Box>
					<TAGButton onClick={handleLoadMore} key={`scheduling-load-more`} variant="secondaryDefault" dataTestId="load-more-button" sx={{
        width: '96px',
        height: '52px',
        padding: 0
      }}>
						Load More
					</TAGButton>
				</Box>}
		</Box>;
}

/**
 * SchedulingTimes component displays available appointment slots for a given facility and brand.
 * It fetches scheduling times from a backend service and shows either today's or tomorrow's availability.
 * If no slots are available, it displays a message and a button to see all times.
 *
 * @param {object} props - The component props.
 * @param {number} props.roundedCornerSize - The size of the rounded corners for the media container.
 * @param {Theme} props.componentTheme - The theme object used for styling the component.
 * @param {SchedulingImageAsset | null} [props.schedulingImageAsset=null] - Optional image asset to display alongside scheduling times.
 * @param {string} props.brand - The brand identifier used for fetching available slots.
 * @param {string} props.facilityId - The facility identifier used for fetching available slots.
 * @param {string} [props.scheduleAppointmentURL=''] - Optional URL to the full appointment scheduling page.
 *
 * @returns {JSX.Element} The rendered SchedulingTimes component.
 */
export default function SchedulingTimes({
  roundedCornerSize,
  componentTheme,
  schedulingImageAsset = null,
  brand,
  facilityId,
  scheduleAppointmentURL = ''
}: Readonly<SchedulingTimesProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    environment,
    config,
    appName,
    appVersion
  } = useAppContext();
  const url = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
  const containerStyles = getMediaContainerStyles({
    theme: componentTheme,
    mediaWithBorderRadius: true,
    imgDisplaysFirstOnDesktop: false,
    mediaRoundedCornerSize: roundedCornerSize
  });
  const wrapperStyles = {
    ...containerStyles,
    ...schedulingTimesWrapperStyles
  };
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [allTimeSlots, setAllTimeSlots] = useState<Record<string, {
    date: string;
    slots: AvailableSlot[];
    isDisabled: boolean;
  }>>({});
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    const getTimes = async () => {
      setLoading(true);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      const availableSlotsResponse = await fetchTimes({
        brand,
        facilityId,
        url,
        appName,
        appVersion,
        startDate,
        endDate
      });
      const selectedTimeSlots = availableSlotsResponse ? availableSlotsResponse[format(selectedDay, 'yyyy-MM-dd')] : undefined;
      let slots = selectedTimeSlots?.slots || [];
      if (slots.length === 0) {
        const firstAvailableDay = Object.entries(availableSlotsResponse ?? {}).find(([_, dayData]) => !dayData.isDisabled);
        if (firstAvailableDay) {
          slots = firstAvailableDay[1].slots;
          setSelectedDay(new Date(`${firstAvailableDay[0]}T00:00:00`));
        }
      }
      setAllTimeSlots(availableSlotsResponse ?? {});
      setAvailableSlots(slots);
      setLoading(false);
    };
    void getTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, facilityId, url, appName, appVersion, startDate]);
  const hasTimes = availableSlots?.length > 0;
  const noSchedulingMessage = 'There are currently no appointments for today';
  const handlePreviousWeek = () => {
    const newDate = new Date(startDate);
    newDate.setDate(startDate.getDate() - 7);
    setStartDate(newDate);
  };
  const handleNextWeek = () => {
    const newDate = new Date(startDate);
    newDate.setDate(startDate.getDate() + 7);
    setStartDate(newDate);
  };
  const handleDaySelect = (date: Date) => {
    setSelectedDay(date);
    setAvailableSlots(allTimeSlots[format(date, 'yyyy-MM-dd')]?.slots || []);
  };
  const isToday = startDate.toDateString() === new Date().toDateString();
  const getWeekDays = (): Date[] => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };
  const getDayAttributes = ({
    isSelected,
    disabled
  }: {
    isSelected: boolean;
    disabled: boolean;
  }) => {
    if (disabled) return {
      backgroundColor: 'transparent',
      textColor: 'text.disabled'
    };
    if (isSelected) return {
      backgroundColor: 'primary.dark',
      textColor: 'text.light'
    };
    return {
      backgroundColor: 'primary.lightest',
      textColor: 'text.primary'
    };
  };
  const weekDays = getWeekDays();
  return <Box data-testid="service_line_hero_scheduling" sx={wrapperStyles} data-sentry-element="Box" data-sentry-component="SchedulingTimes" data-sentry-source-file="SchedulingTimes.tsx">
			{schedulingImageAsset && <Box data-testid="scheduling-image-asset-wrapper" sx={schedulingTimesImageStyles}>
					<ImageAsset desktopImage={schedulingImageAsset.desktopImage as IImage} mobileImage={schedulingImageAsset.mobileImage as IImage} isBackgroundImage={false} />
				</Box>}
			<Box sx={schedulingTimesContentWrapperStyles} data-sentry-element="Box" data-sentry-source-file="SchedulingTimes.tsx">
				<Typography variant="header4" mb="1.25rem" data-sentry-element="Typography" data-sentry-source-file="SchedulingTimes.tsx">
					Schedule a visit
				</Typography>

				<Box data-testid="scheduling-times-date-selector" sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        justifyContent: 'center'
      }} data-sentry-element="Box" data-sentry-source-file="SchedulingTimes.tsx">
					<IconButton onClick={handlePreviousWeek} disabled={isToday} aria-label="Previous week" data-testid="previous-week-button" size="small" sx={{
          color: 'text.primary'
        }} data-sentry-element="IconButton" data-sentry-source-file="SchedulingTimes.tsx">
						<ChevronLeft data-sentry-element="ChevronLeft" data-sentry-source-file="SchedulingTimes.tsx" />
					</IconButton>
					<Typography variant="bodyMediumBook" color="text.primary" sx={{
          textAlign: 'center'
        }} data-sentry-element="Typography" data-sentry-source-file="SchedulingTimes.tsx">
						{getWeekOut(startDate)}
					</Typography>
					<IconButton onClick={handleNextWeek} aria-label="Next week" data-testid="next-week-button" size="small" sx={{
          color: 'text.primary'
        }} data-sentry-element="IconButton" data-sentry-source-file="SchedulingTimes.tsx">
						<ChevronRight data-sentry-element="ChevronRight" data-sentry-source-file="SchedulingTimes.tsx" />
					</IconButton>
				</Box>

				<Box data-testid="week-days-selector" sx={{
        display: 'flex',
        ...(isMobile ? {
          justifyContent: 'space-between'
        } : {
          gap: '0.75rem',
          justifyContent: 'center'
        })
      }} data-sentry-element="Box" data-sentry-source-file="SchedulingTimes.tsx">
					{weekDays.map(day => {
          const isSelected = day.toDateString() === selectedDay.toDateString();
          const dayName = day.toLocaleDateString('en-US', {
            weekday: 'short'
          });
          const dayNumber = day.getDate();
          const disabled = allTimeSlots[format(day, 'yyyy-MM-dd')]?.isDisabled ?? true;
          const {
            backgroundColor,
            textColor
          } = getDayAttributes({
            isSelected,
            disabled
          });
          return <Box key={day.toISOString()} onClick={() => {
            if (disabled) return;
            handleDaySelect(day);
          }} data-testid={`day-selector-${dayNumber}`} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: disabled ? 'default' : 'pointer',
            minWidth: '48px'
          }}>
								<Typography variant="bodySmallBook" sx={{
              color: 'text.tertiary',
              mb: 0.5
            }}>
									{dayName}
								</Typography>
								<Box sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor,
              userSelect: 'none'
            }}>
									<Typography variant="bodyMediumBold" color={textColor}>
										{dayNumber}
									</Typography>
								</Box>
							</Box>;
        })}
				</Box>

				{!hasTimes && <Box data-testid="scheduling-times-message" component="p" sx={{
        color: 'text.secondary'
      }}>
						<Typography data-testid="no-scheduling-message" sx={{
          mb: '1rem'
        }}>
							{noSchedulingMessage}
						</Typography>
					</Box>}

				{hasTimes ? <>
						<SchedulingTimesList slotsList={availableSlots} scheduleAppointmentURL={scheduleAppointmentURL} facilityId={facilityId} loading={loading} />
						<Box component="p" style={timeMessageStyles}>
							<Typography variant="bodyMediumBook" sx={{
            color: componentTheme.palette.text.black
          }}>
								Not seeing a time that works for you?
							</Typography>{' '}
							<TAGLink dataTestId="see-all-times-link" href={scheduleAppointmentURL ?? undefined}>
								See all times
							</TAGLink>
						</Box>
					</> : <Box id="no-times-available-button-section" sx={{
        mt: '1.25rem'
      }}>
						<TAGButton dataTestId="see-all-times-button" href={scheduleAppointmentURL ?? ''} variant="primaryDefault" size="M" sx={{
          mt: '1.25rem'
        }}>
							See all times
						</TAGButton>
					</Box>}
			</Box>
		</Box>;
}